import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DisplayModus } from 'constants/constants'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronUp, faChevronDown, faArrowUp } from '@fortawesome/free-solid-svg-icons'
import { faSquarePlus } from '@fortawesome/free-regular-svg-icons'
import ModalInstallationRemoval from './modalInstallationRemoval'
import { Lang } from 'context/LangContext'
import {
  Collapsible,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@airbus/components-react/components'
import Fieldset from 'components/Fieldset/fieldset'
import { InstallationRemoval, InstallationRemovalInput } from 'types/workorder/installationRemoval'
import useQuery from 'utils/hooks/useQuery'
import urlApis from 'utils/api/urlApis'

type Props = {
  lang: Lang
  oenumdoc: string | null
  displayMode: DisplayModus
  expandAll: boolean
}

type SelectedInstallationRemoval = InstallationRemoval & {
  checked?: boolean
  open?: boolean
  loading?: boolean
  errorMsg?: string
  done?: boolean
  date?: Date
  resourceType?: string
}

const WoInstallationsRemovals = ({ lang, oenumdoc, displayMode, expandAll }: Props) => {
  const { t } = useTranslation()

  const [isModalFormOpen, setIsModalFormOpen] = useState<boolean>(false)
  const [selectedResource, setSelectedResource] = useState<InstallationRemovalInput>()

  const [data, setData] = useState<SelectedInstallationRemoval[]>()
  const { fetchData: fetchInstallationRemovals } = useQuery<InstallationRemoval[]>(
    urlApis.URL_WORKORDER_INSTALLATION_REMOVAL_GET,
  )

  // Expand/collapse status of main component
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  // Data grouped by bonumdoc1+'bonumdoc2': {<bonum_1>: [{...}, {...}], <bonum_2>: [{...}, {...}]}
  const [dataGroupedByType, setDataGroupedByType] = useState({})
  // Expand/collapse status of grouped-by type tables
  const [isExpandedGroupedByType, setIsExpandedGroupedByType] = useState({})

  const handleModalFormPostSubmit = () => {
    setIsModalFormOpen(false)
    setSelectedResource(undefined)

    loadData()
  }

  const handleCreateInstallationRemoval = () => {
    setSelectedResource({
      bonumdoc1: '',
      bonumdoc2: '',
      oenumdoc1: oenumdoc?.substring(0, 4) as string,
      oenumdoc2: oenumdoc?.substring(4) as string,
      //dateDepose?: number[]
      //        datePose?: number[]
    })
    setIsModalFormOpen(true)
  }

  const handleUpdateInstallationRemoval = (resourceType: any, j: number) => {
    const nextResource = dataGroupedByType[resourceType].map((r: any, i) =>
      i === j ? { ...r, checked: true } : { ...r, checked: false },
    )
    setSelectedResource(nextResource)
    setModalFormDisplayMode(DisplayModus.WRITE)
    setIsModalFormOpen(true)
  }

  const loadData = async () => {
    if (oenumdoc) {
      const response = await fetchInstallationRemovals({
        lang: lang,
        oenumdoc1: oenumdoc?.substring(0, 4),
        oenumdoc2: oenumdoc?.substring(4),
      })
      setData(response?.data?.content)
    }
  }

  /**
   * Load installations-removals of Work-order
   */
  useEffect(() => {
    if (isExpanded) {
      loadData()
    } else {
      setData([])
    }
  }, [lang, isExpanded])

  /**
   * Group by data (required resources) by type (keep ordering as it is)
   */
  useEffect(() => {
    if (data) {
      const dataGroupByType = {}
      const isExpandedGroupByType = {}
      data.forEach((objResource) => {
        if (
          Object.prototype.hasOwnProperty.call(dataGroupByType, objResource['bonumdoc1'] + objResource['bonumdoc2'])
        ) {
          dataGroupByType[objResource['bonumdoc1'] + objResource['bonumdoc2']].push(objResource)
        } else {
          dataGroupByType[objResource['bonumdoc1'] + objResource['bonumdoc2']] = [objResource]
          isExpandedGroupByType[objResource['bonumdoc1'] + objResource['bonumdoc2']] = isExpanded
        }
      })
      setDataGroupedByType(dataGroupByType)
      setIsExpandedGroupedByType(isExpandedGroupByType)
    } else {
      setDataGroupedByType({})
      setIsExpandedGroupedByType({})
    }
  }, [data])

  /**
   * ExpandAll and collapseAll are mirror-toggled, so a hook on expandAll is enough
   */
  useEffect(() => {
    setIsExpanded(expandAll)

    if (JSON.stringify(isExpandedGroupedByType) !== '{}') {
      const copyIsExpandedGroupedByType = { ...isExpandedGroupedByType }
      Object.keys(copyIsExpandedGroupedByType).map((it) => (copyIsExpandedGroupedByType[it] = expandAll))
      setIsExpandedGroupedByType(copyIsExpandedGroupedByType)
    }
  }, [expandAll])

  const handleSubSectionToggleExpandCollapse = (evt) => {
    const actionName = evt.currentTarget.attributes['name'].nodeValue
    const copyIsExpandedGroupedByType = { ...isExpandedGroupedByType }
    copyIsExpandedGroupedByType[actionName] = !isExpandedGroupedByType[actionName]
    setIsExpandedGroupedByType(copyIsExpandedGroupedByType)
  }

  return (
    <div style={{ width: '100%' }}>
      <Collapsible
        title={t('7009088') + '/' + t('7009087')}
        expanded={isExpanded}
        onChange={() => setIsExpanded(!isExpanded)}
      >
        <div style={{ display: isExpanded ? 'inherit' : 'none' }}>
          {JSON.stringify(dataGroupedByType) !== '{}' && JSON.stringify(dataGroupedByType) !== '[]' ? (
            <div>
              {Object.keys(dataGroupedByType).map((resourceType, i) => (
                <Fieldset
                  legend={
                    <>
                      {isExpandedGroupedByType[resourceType] ? (
                        <FontAwesomeIcon
                          name={resourceType}
                          icon={faChevronUp}
                          onClick={handleSubSectionToggleExpandCollapse}
                        />
                      ) : (
                        <FontAwesomeIcon
                          name={resourceType}
                          icon={faChevronDown}
                          onClick={handleSubSectionToggleExpandCollapse}
                        />
                      )}
                      {t('0MOE063') + ' ' + resourceType}
                    </>
                  }
                  key={'installationRemovalNum_' + i}
                >
                  <div
                    style={{
                      display: isExpandedGroupedByType[resourceType] ? 'inherit' : 'none',
                    }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell colSpan={2} style={{ width: '5em' }}>
                            {t('9500034')}
                          </TableCell>
                          <TableCell style={{ width: '14em' }}>{t('9500047')}</TableCell>
                          <TableCell style={{ width: '14em' }}>{t('9500048')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.prototype.hasOwnProperty.call(dataGroupedByType[resourceType][0], 'dateDepose') &&
                        Object.prototype.hasOwnProperty.call(dataGroupedByType[resourceType][0], 'pnDepose') ? (
                          <TableRow key={'removalNum_' + i}>
                            <TableCell style={{ width: '1em' }}>
                              <FontAwesomeIcon
                                icon={faArrowUp}
                                style={{
                                  transform: 'rotate(135deg)',
                                  color: 'red',
                                }}
                                title={t('0MBO009')}
                              />
                            </TableCell>
                            <TableCell style={{ width: '4em' }}>
                              {dataGroupedByType[resourceType][0].dateDepose
                                ? ('0' + dataGroupedByType[resourceType][0].dateDepose[2]).slice(-2) +
                                  '/' +
                                  ('0' + dataGroupedByType[resourceType][0].dateDepose[1]).slice(-2) +
                                  '/' +
                                  dataGroupedByType[resourceType][0].dateDepose[0]
                                : '-'}
                            </TableCell>
                            <TableCell>
                              {dataGroupedByType[resourceType][0].pnDepose
                                ? dataGroupedByType[resourceType][0].pnDepose
                                : '-'}
                            </TableCell>
                            <TableCell>
                              {dataGroupedByType[resourceType][0].snDepose
                                ? dataGroupedByType[resourceType][0].snDepose
                                : '-'}
                            </TableCell>
                          </TableRow>
                        ) : (
                          ''
                        )}

                        {Object.prototype.hasOwnProperty.call(dataGroupedByType[resourceType][0], 'datePose') &&
                        Object.prototype.hasOwnProperty.call(dataGroupedByType[resourceType][0], 'pnPose') ? (
                          <TableRow key={'installationNum_' + i}>
                            <TableCell style={{ width: '1em' }}>
                              <FontAwesomeIcon
                                icon={faArrowUp}
                                style={{
                                  transform: 'rotate(45deg)',
                                  color: 'green',
                                }}
                                title={t('0MBO018')}
                              />
                            </TableCell>
                            <TableCell style={{ width: '4em' }}>
                              {dataGroupedByType[resourceType][0].datePose
                                ? ('0' + dataGroupedByType[resourceType][0].datePose[2]).slice(-2) +
                                  '/' +
                                  ('0' + dataGroupedByType[resourceType][0].datePose[1]).slice(-2) +
                                  '/' +
                                  dataGroupedByType[resourceType][0].datePose[0]
                                : '-'}
                            </TableCell>
                            <TableCell>
                              {dataGroupedByType[resourceType][0].pnPose
                                ? dataGroupedByType[resourceType][0].pnPose
                                : '-'}
                            </TableCell>
                            <TableCell>
                              {dataGroupedByType[resourceType][0].snPose
                                ? dataGroupedByType[resourceType][0].snPose
                                : '-'}
                            </TableCell>
                          </TableRow>
                        ) : (
                          ''
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </Fieldset>
              ))}
            </div>
          ) : (
            <div style={{ paddingLeft: '1em' }}>{t('9500055')}</div>
          )}
        </div>
      </Collapsible>

      <ModalInstallationRemoval
        lang={lang}
        open={isModalFormOpen}
        resource={selectedResource}
        onClose={() => setIsModalFormOpen(false)}
        oenumdoc1={oenumdoc?.substring(0, 4)}
        oenumdoc2={oenumdoc?.substring(4)}
      />
    </div>
  )
}

export default WoInstallationsRemovals
